import { useCallback, useEffect, useRef, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import {
  fetchChatHistory,
  sendChatMessage,
  clearChatHistory,
} from '../lib/fieldAssistantApi.js';

const SHORTCUTS = [
  { label: "What's next?", message: 'What is next on my itinerary today?' },
  { label: 'Tomorrow weather', message: 'What is the weather forecast for tomorrow in Juneau?' },
  { label: 'My points', message: 'How many points do I have so far and from which submissions?' },
  { label: 'Set reminder', message: 'Help me set a reminder for tomorrow morning.' },
];

export default function FieldAssistant() {
  const { tripId, trip } = useTrip();
  const session = useSession();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);

  const meId = session?.role === 'player' ? session.recipient_id : null;
  const me = (trip?.players || []).find((p) => p.id === meId) || null;

  const loadHistory = useCallback(async () => {
    if (!meId) {
      setHistoryLoaded(true);
      return;
    }
    try {
      const res = await fetchChatHistory({ tripId });
      setMessages(res.history || []);
    } catch (err) {
      // Plain `vite` dev or no-session — leave empty, no toast.
      if (err.status !== 401 && err.status !== 'history_unavailable') {
        setError(err.message || String(err));
      }
    } finally {
      setHistoryLoaded(true);
    }
  }, [tripId, meId]);

  useEffect(() => {
    if (!session.loading) loadHistory();
  }, [session.loading, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending]);

  const send = useCallback(
    async (text) => {
      if (!text.trim() || pending || !meId) return;
      setError(null);
      setPending(true);
      const optimistic = [...messages, { role: 'user', content: text }];
      setMessages(optimistic);
      setInput('');
      try {
        const res = await sendChatMessage({ tripId, message: text });
        if (res.blocked) {
          setError(res.message || 'Daily limit reached.');
        }
        setMessages([
          ...optimistic,
          {
            role: 'assistant',
            content: res.reply || '(no response)',
            tool_calls: res.tool_calls || [],
          },
        ]);
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setPending(false);
      }
    },
    [tripId, meId, messages, pending]
  );

  const onClear = useCallback(async () => {
    if (!meId) return;
    if (!window.confirm('Clear your assistant chat history?')) return;
    try {
      await clearChatHistory({ tripId });
      setMessages([]);
    } catch (err) {
      setError(err.message || String(err));
    }
  }, [tripId, meId]);

  return (
    <div className="mx-auto flex h-full w-full max-w-screen-sm flex-col p-4">
      <header className="space-y-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          private · only you can see this
        </p>
        <div className="flex items-baseline justify-between gap-2">
          <h1 className="font-serif text-2xl">Field Assistant</h1>
          {messages.length > 0 && meId && (
            <button
              type="button"
              onClick={onClear}
              className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline"
            >
              clear chat
            </button>
          )}
        </div>
        <p className="text-sm text-fg-secondary">
          Per-player Claude chat. Knows the itinerary, weather, tides, and your own submissions.
          Doesn&apos;t see anyone else&apos;s data.
        </p>
      </header>

      {!session.loading && !meId && (
        <div className="card mt-4 border-copper/40 bg-copper/5 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-copper-dark">
            sign-in required
          </p>
          <p className="mt-1 text-sm text-fg-secondary">
            Open the app from your briefing-email link to start a chat.
          </p>
        </div>
      )}

      {meId && (
        <>
          <div
            ref={scrollRef}
            className="scroll-area mt-3 flex-1 space-y-3 overflow-y-auto rounded-md border border-border-tertiary bg-bg-primary p-3"
          >
            {historyLoaded && messages.length === 0 && (
              <Empty meName={me?.display_name || me?.name} />
            )}
            {messages.map((m, i) => (
              <Message key={i} m={m} meName={me?.display_name || me?.name} />
            ))}
            {pending && (
              <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                …assistant typing
              </p>
            )}
          </div>

          {error && (
            <div className="mt-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
              {error}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-1.5">
            {SHORTCUTS.map((s) => (
              <button
                key={s.label}
                type="button"
                disabled={pending}
                onClick={() => send(s.message)}
                className="rounded-full border border-border-tertiary bg-bg-secondary px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-secondary transition hover:bg-bg-tertiary disabled:opacity-50"
              >
                {s.label}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="mt-3 flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything trip-related…"
              disabled={pending}
              className="flex-1 rounded-md border border-border-tertiary bg-bg-primary px-3 py-2 text-sm placeholder:text-fg-tertiary focus:border-action focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={pending || !input.trim()}
              className="rounded-md border border-action bg-action px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-action-fg transition disabled:opacity-50"
            >
              {pending ? '…' : 'send'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

function Empty({ meName }) {
  return (
    <div className="space-y-2 py-6 text-center font-serif italic text-fg-secondary">
      <p>Hello{meName ? `, ${meName}` : ''}.</p>
      <p className="not-italic font-sans text-sm text-fg-tertiary">
        Try a shortcut below, or ask anything about the trip.
      </p>
    </div>
  );
}

function Message({ m, meName }) {
  const isUser = m.role === 'user';
  return (
    <div className={['flex', isUser ? 'justify-end' : 'justify-start'].join(' ')}>
      <div
        className={[
          'max-w-[80%] space-y-1 rounded-md px-3 py-2 text-sm',
          isUser
            ? 'bg-action/15 text-fg-primary'
            : 'border border-border-tertiary bg-bg-secondary text-fg-primary',
        ].join(' ')}
      >
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {isUser ? meName || 'you' : 'assistant'}
        </p>
        <p className="whitespace-pre-wrap">{m.content}</p>
        {m.tool_calls?.length > 0 && (
          <ul className="mt-1 space-y-0.5 border-t border-border-tertiary pt-1">
            {m.tool_calls.map((tc, i) => (
              <li
                key={i}
                className="font-mono text-[10px] text-fg-tertiary"
              >
                {tc.ok ? '✓' : '✕'} {tc.name}
                {tc.input && Object.keys(tc.input).length > 0
                  ? ` (${Object.entries(tc.input)
                      .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
                      .join(', ')})`
                  : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
